import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { t } from 'i18next';

import {
    Breadcrumbs,
    BreadcrumbsItem,
    MenuItem,
    OutlineButton,
    TextField,
    Typography,
    useSnackbar,
} from '@bb-ui/react-library';
import { AlertError } from '@bb-ui/icons/dist/small';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { Trans } from 'react-i18next';
import { useStyles } from './CreateUserPage.styles';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { useAuth0Context } from '../../../../auth/auth0/Auth0Context';
import { getTenantId } from '../../../../utilities/utilities';
import { useApplicationTitle } from '../../../../hooks/useApplicationTitle';
import { ResponsiveBox } from '../../../ResponsiveBox/ResponsiveBox';
import { Frame } from '../../../Frame/Frame';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { LoadingIndicator } from '../../../LoadingIndicator/LoadingIndicator';
import { EditUserError } from '../EditPage/EditUserError/EditUserError';
import { useIdentityProviderContext } from '../../../../contexts/IdentityProviderContext';
import { useLicenseManagementContext } from '../../../../contexts/LicenseManagementContext';
import { User } from '../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { useFeatureContext } from '../../../../contexts/FeatureContext';
import { FeatureName } from '../../../../featureFlags/Feature.types';
import {
    USERNAME_MAX_LENGTH,
    usernameProblem,
} from '../../../../utilities/usernameRules';

const DEFAULT_IDP_VALUE = 'default';

export const CreateUserPage: FunctionComponent = () => {
    const userRoleManagementEnabled =
        useFeatureContext()[
            'bbdata.roles.permissions.user.role.management' as FeatureName
        ]; // 2593750 Manage roles in Illuminate
    const classes = useStyles();
    const history = useHistory();
    const { uas, api } = useAppConfigContext();

    const { user: auth0ContextUser } = useAuth0Context();
    const tenantId = getTenantId(auth0ContextUser, uas);
    const { enqueueSnackbar } = useSnackbar();

    const userApiUrl = encodeURI(
        `${api!.userManagement}/v2/tenants/${tenantId}/users`,
    );

    const [usernameValue, setUsernameValue] = useState<string>('');
    const [selectedIdpValue, setSelectedIdpValue] =
        useState<string>(DEFAULT_IDP_VALUE);
    const [usernameError, setUsernameError] = useState<string>('');
    const [idpError, setIdpError] = useState<string>('');
    const [submitClicked, setSubmitClicked] = useState<boolean>(false);

    const [userAddError, setUserAddError] = useState<string>('');

    const {
        idps: tenantIdps,
        error: idpApiError,
        loaded: idpApiLoaded,
    } = useIdentityProviderContext();
    const { addUsers } = useLicenseManagementContext();

    const userApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: userApiUrl,
            method: 'POST',
            useAuth: true,
            executeType: 'manual',
            data: {
                users: [
                    {
                        identityProviderId: selectedIdpValue,
                        userName: usernameValue,
                    },
                ],
            },
        }),
        [selectedIdpValue, userApiUrl, usernameValue],
    );

    const {
        data: createUserData,
        loading: createUserLoading,
        error: createUserError,
        execute: createUserExecute,
    } = useApi<{ users: User[] }>(userApiConfig);

    useApplicationTitle(t('settings.users.create.title'));

    const handleExitClick = (): undefined => {
        history.push(`/settings/`);
        return undefined;
    };

    const validateUsernameValue = (value: string): boolean => {
        const problem = usernameProblem(value);
        if (!problem) return true;

        switch (problem.kind) {
            case 'required':
                setUsernameError(t('settings.users.field.requiredError'));
                break;
            case 'tooLong':
                setUsernameError(
                    t('settings.users.field.usernameTooLong', {
                        maxLength: t('general.number', {
                            value: USERNAME_MAX_LENGTH,
                        }),
                    }),
                );
                break;
            case 'firstCharInvalid':
                setUsernameError(t('settings.users.field.firstCharInvalid'));
                break;
            case 'lastCharInvalid':
                setUsernameError(t('settings.users.field.lastCharInvalid'));
                break;
            case 'invalidCharacters':
                setUsernameError(
                    t('settings.users.field.wrongCharacterSetError', {
                        characters: problem.characters
                            .map(char => `'${char}'`)
                            .join(', '),
                    }),
                );
                break;
        }
        return false;
    };

    const validateIdpValue = (value: string): boolean => {
        if (value === DEFAULT_IDP_VALUE) {
            setIdpError(t('settings.users.field.requiredError'));
            return false;
        }
        return true;
    };

    const handleUsernameChange = (value: string): void => {
        setUsernameError('');
        const valueClean = value.trim();

        setUsernameValue(valueClean);
        validateUsernameValue(valueClean); // instant feedback
    };

    const handleIdpChange = (value: string): void => {
        setIdpError('');
        setSelectedIdpValue(value);
        validateIdpValue(value); // instant feedback
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const usernameValid = validateUsernameValue(usernameValue);
        const idpValid = validateIdpValue(selectedIdpValue);

        if (usernameValid && idpValid) {
            setSubmitClicked(true);
            createUserExecute();
        }
        // do nothing if fields are invalid
    };

    useEffect(() => {
        // after the idps are fetched/errored, handle it
        if (idpApiError) {
            enqueueSnackbar(t('settings.users.create.idpApi.error'));
        }
    }, [enqueueSnackbar, idpApiError]);

    useEffect(() => {
        if (tenantIdps && tenantIdps.length === 1) {
            setSelectedIdpValue(tenantIdps[0].id);
        }
    }, [tenantIdps]);

    useEffect(() => {
        if (
            submitClicked &&
            !createUserLoading &&
            (createUserData !== undefined || createUserError !== null)
        ) {
            if (createUserError) {
                if (createUserError.status === 409) {
                    setUserAddError(
                        t('settings.users.create.existingUserErrorMessage'),
                    );
                }
                enqueueSnackbar(t('settings.users.create.snackbar.error'));
            } else if (createUserData) {
                addUsers(createUserData?.users);
                enqueueSnackbar(t('settings.users.create.snackbar.success'));

                if (createUserData.users) {
                    const url = `/settings/users/${encodeURIComponent(createUserData.users[0].userId)}/edit/`;
                    history.push(url.replace(/%40/g, '@'));
                }
            }
            setSubmitClicked(false);
        }
    }, [
        createUserData,
        createUserError,
        createUserLoading,
        enqueueSnackbar,
        history,
        submitClicked,
        addUsers,
    ]);

    const createUserLoader = (
        <div className={classes.loadingIndicator}>
            <LoadingIndicator
                label={t('settings.users.create.loaderCreatingUser')}
                id="user-data-loading-indicator"
            />
        </div>
    );

    const initialLoader = (
        <div className={classes.loadingIndicator}>
            <LoadingIndicator
                label={t('settings.users.create.loaderFetchingData')}
                id="tenant-data-loading-indicator"
            />
        </div>
    );

    const addUserErrorMessage = (
        <Banner
            severity="warning"
            icon={<AlertError color="primary" />}
            tabIndex={0} // for AX purpose, so that focus would be on Banner with error message
            data-testid="user-add-error"
            aria-live="polite"
            className={classes.errorMessage}
        >
            {userAddError}
        </Banner>
    );

    const createUserForm = (
        <form onSubmit={handleFormSubmit}>
            <div className={classes.usernameTextFieldWrapper}>
                <TextField
                    id="username-input"
                    data-testid="username-input"
                    className={classes.usernameTextField}
                    label={t('settings.users.create.usernameField.label')}
                    placeholder={t(
                        'settings.users.create.usernameField.placeholder',
                    )}
                    value={usernameValue}
                    onChange={event => {
                        handleUsernameChange(event.target.value.trim());
                    }}
                    error={!!usernameError.length}
                    helperText={usernameError}
                />
            </div>
            <div>
                <TextField
                    select
                    id="idp-select"
                    data-testid="idp-select"
                    label={t('settings.users.create.idpField.label')}
                    inputProps={{
                        'data-testid': 'select-idp',
                    }}
                    // floatingLabel={true}
                    value={selectedIdpValue}
                    onChange={event => {
                        handleIdpChange(event.target.value);
                    }}
                    className={classes.idpSelectField}
                    error={!!idpError.length}
                    helperText={idpError}
                >
                    {/* The default select value */}
                    {tenantIdps && (
                        <MenuItem value={DEFAULT_IDP_VALUE}>
                            {t('settings.users.create.idpField.placeholder')}
                        </MenuItem>
                    )}
                    {tenantIdps &&
                        tenantIdps.map(option => (
                            <MenuItem button key={option.id} value={option.id}>
                                {option.displayName}
                            </MenuItem>
                        ))}
                </TextField>
            </div>
            <div>
                <OutlineButton
                    data-testid="create-user-button"
                    className={classes.createUserButton}
                    type="submit"
                >
                    {t('settings.licenseManagement.createUserButton')}
                </OutlineButton>
            </div>
            {userAddError && addUserErrorMessage}
        </form>
    );

    // main template
    return (
        <>
            <ResponsiveBox className={classes.mainBox}>
                <Breadcrumbs
                    menuButtonLabel={t(
                        'settings.users.breadCrumbs.menuButtonAriaLabel',
                    )}
                    menuId="menu-id-1"
                    navigationLabel={t('settings.users.breadCrumbs.ariaLabel')}
                    isCurrentLinkActive={false}
                    data-testid="create-user-breadcrumbs"
                >
                    <BreadcrumbsItem label="" isCurrentPage={false}>
                        <a
                            data-testid="create-user-breadcrumbs-back-link"
                            onClick={handleExitClick}
                            className={classes.breadcrumbLink}
                            href="#"
                        >
                            {t(
                                'settings.users.breadCrumbs.userManagementBreadcrumb',
                            )}
                        </a>
                    </BreadcrumbsItem>
                    <BreadcrumbsItem label="" isCurrentPage={true}>
                        <a href="#">{t('settings.users.create.title')}</a>
                    </BreadcrumbsItem>
                </Breadcrumbs>
            </ResponsiveBox>
            <ResponsiveBox
                className={classes.mainBox}
                dataTestId="create-user-page-main-box"
            >
                <Frame className={classes.paper}>
                    {!idpApiLoaded ? (
                        initialLoader
                    ) : (
                        <>
                            {createUserLoading ? (
                                createUserLoader
                            ) : (
                                <>
                                    <Typography
                                        variant="h2"
                                        component="h1"
                                        className={classes.searchAndCreate}
                                    >
                                        {t(
                                            'settings.users.create.searchAndCreate',
                                        )}
                                    </Typography>
                                    <Typography
                                        component="p"
                                        className={
                                            classes.searchAndCreateSubtext
                                        }
                                    >
                                        {userRoleManagementEnabled ? (
                                            /* prettier-ignore */
                                            <Trans i18nKey="settings.users.create.searchAndCreateSubtext">
                                            Enter the exact username and Identity Provider (IdP) to grant an
                                            existing <span lang="en">Blackboard</span> user access
                                            to <span lang="en">Illuminate</span>.
                                        </Trans>
                                        ) : (
                                            /* prettier-ignore */
                                            <Trans i18nKey="settings.users.create.searchAndCreateSubtextFTUE">
                                            Enter the exact username and Identity Provider (IdP) to grant an
                                            existing <span lang="en">Blackboard</span> user access
                                            to <span lang="en">Illuminate</span>. You must assign the
                                            user an <span lang="en">Illuminate</span> role in
                                            <span lang="en">Blackboard</span> Administrator panel or by
                                            adding the user to the corresponding <span lang="en">SAML</span> groups.
                                        </Trans>
                                        )}
                                    </Typography>

                                    {idpApiError ? (
                                        <EditUserError error="An error occurred while loading the data." />
                                    ) : (
                                        createUserForm
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Frame>
            </ResponsiveBox>
        </>
    );
};
